/**
 * Redux Slice for Learning Content Discovery Hub
 * Feature: 029 - Learning Content Discovery Hub
 *
 * Manages state for the Netflix/Prime Video-style content discovery interface.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import {
  DiscoveryHubState,
  FeaturedContentItem,
  ContentRow,
  ContentFilter,
  DiscoveryContentItem,
  RowType,
  ViewMode,
  TradeRowState,
  Trade,
  BundleRowState,
} from "../types";
import type { DashboardStripsData } from "../api/fetchDashboardStrips";
import type { BundleWithContent } from "../api/fetchBundleContent";

/**
 * Initial state for the Discovery Hub
 */
const initialState: DiscoveryHubState = {
  featured: {
    items: [],
    loading: false,
    error: null,
  },
  dashboardStrips: {
    memberNews: [],
    featuredItems: [],
    featuredResources: [],
    favorites: [],
    loading: false,
    error: null,
  },
  rows: {
    continuelearning: null,
    programs: null,
    learningPaths: null,
    courses: null,
    events: null,
    resources: null,
  },
  trades: {
    available: [],
    rows: {},
    loading: false,
    error: null,
  },
  bundles: {
    rows: {},
    loading: false,
    error: null,
  },
  ui: {
    activeFilter: "all",
    viewMode: "discover",
    heroIndex: 0,
    searchQuery: "",
    searchResults: null,
    searchLoading: false,
    isSearchOpen: false,
  },
  loading: {
    featured: false,
    continuelearning: false,
    programs: false,
    learningPaths: false,
    courses: false,
    events: false,
    resources: false,
  },
  errors: {
    featured: null,
    continuelearning: null,
    programs: null,
    learningPaths: null,
    courses: null,
    events: null,
    resources: null,
  },
};

const discoveryHubSlice = createSlice({
  name: "discoveryHub",
  initialState,
  reducers: {
    // Featured content actions
    setFeaturedContent: (
      state,
      action: PayloadAction<FeaturedContentItem[]>,
    ) => {
      state.featured.items = action.payload;
    },
    setFeaturedLoading: (state, action: PayloadAction<boolean>) => {
      state.featured.loading = action.payload;
    },
    setFeaturedError: (state, action: PayloadAction<string | null>) => {
      state.featured.error = action.payload;
    },

    // Content row actions
    setContentRow: (
      state,
      action: PayloadAction<{ type: RowType; row: ContentRow | null }>,
    ) => {
      state.rows[action.payload.type] = action.payload.row;
    },

    // Loading state per row
    setRowLoading: (
      state,
      action: PayloadAction<{ type: RowType; loading: boolean }>,
    ) => {
      state.loading[action.payload.type] = action.payload.loading;
    },

    // Error state per row
    setRowError: (
      state,
      action: PayloadAction<{ type: RowType; error: string | null }>,
    ) => {
      state.errors[action.payload.type] = action.payload.error;
    },

    // UI actions
    setActiveFilter: (state, action: PayloadAction<ContentFilter>) => {
      state.ui.activeFilter = action.payload;
    },
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.ui.viewMode = action.payload;
    },
    setHeroIndex: (state, action: PayloadAction<number>) => {
      state.ui.heroIndex = action.payload;
    },
    nextHeroSlide: (state) => {
      const maxIndex = state.featured.items.length - 1;
      state.ui.heroIndex =
        state.ui.heroIndex >= maxIndex ? 0 : state.ui.heroIndex + 1;
    },
    prevHeroSlide: (state) => {
      const maxIndex = state.featured.items.length - 1;
      state.ui.heroIndex =
        state.ui.heroIndex <= 0 ? maxIndex : state.ui.heroIndex - 1;
    },

    // Search actions
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.ui.searchQuery = action.payload;
    },
    setSearchResults: (
      state,
      action: PayloadAction<DiscoveryContentItem[] | null>,
    ) => {
      state.ui.searchResults = action.payload;
    },
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.ui.searchLoading = action.payload;
    },
    setSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.isSearchOpen = action.payload;
    },
    clearSearch: (state) => {
      state.ui.searchQuery = "";
      state.ui.searchResults = null;
      state.ui.searchLoading = false;
    },

    // Reset actions
    resetState: (state) => {
      Object.assign(state, initialState);
    },

    // Dashboard strips actions
    setDashboardStrips: (state, action: PayloadAction<DashboardStripsData>) => {
      state.dashboardStrips.memberNews = action.payload.memberNews;
      state.dashboardStrips.featuredItems = action.payload.featuredItems;
      state.dashboardStrips.featuredResources =
        action.payload.featuredResources;
      state.dashboardStrips.favorites = action.payload.favorites;
    },
    setDashboardStripsLoading: (state, action: PayloadAction<boolean>) => {
      state.dashboardStrips.loading = action.payload;
    },
    setDashboardStripsError: (state, action: PayloadAction<string | null>) => {
      state.dashboardStrips.error = action.payload;
    },

    // Trade-related actions
    setAvailableTrades: (state, action: PayloadAction<Trade[]>) => {
      state.trades.available = action.payload;
    },
    setTradesLoading: (state, action: PayloadAction<boolean>) => {
      state.trades.loading = action.payload;
    },
    setTradesError: (state, action: PayloadAction<string | null>) => {
      state.trades.error = action.payload;
    },
    setTradeRow: (state, action: PayloadAction<TradeRowState>) => {
      state.trades.rows[action.payload.tradeId] = action.payload;
    },
    setTradeRowLoading: (
      state,
      action: PayloadAction<{ tradeId: number; loading: boolean }>,
    ) => {
      const row = state.trades.rows[action.payload.tradeId];
      if (row) {
        row.loading = action.payload.loading;
      }
    },
    setTradeRowError: (
      state,
      action: PayloadAction<{ tradeId: number; error: string | null }>,
    ) => {
      const row = state.trades.rows[action.payload.tradeId];
      if (row) {
        row.error = action.payload.error;
      }
    },

    // Bundle-related actions
    setBundlesLoading: (state, action: PayloadAction<boolean>) => {
      state.bundles.loading = action.payload;
    },
    setBundlesError: (state, action: PayloadAction<string | null>) => {
      state.bundles.error = action.payload;
    },
    setBundleRow: (state, action: PayloadAction<BundleRowState>) => {
      state.bundles.rows[action.payload.bundleId] = action.payload;
    },
    setBundleRows: (state, action: PayloadAction<BundleWithContent[]>) => {
      // Reset rows and populate with new data
      state.bundles.rows = {};
      action.payload.forEach((bundle) => {
        state.bundles.rows[bundle.bundleId] = {
          bundleId: bundle.bundleId,
          bundleName: bundle.bundleName,
          bundleDescription: bundle.bundleDescription,
          resourceTags: bundle.resourceTags,
          items: bundle.items,
          loading: bundle.loading,
          error: bundle.error,
          hasContent: bundle.items.length > 0,
        };
      });
    },
    clearBundleRows: (state) => {
      state.bundles.rows = {};
    },
  },
});

// Export actions
export const {
  setFeaturedContent,
  setFeaturedLoading,
  setFeaturedError,
  setContentRow,
  setRowLoading,
  setRowError,
  setActiveFilter,
  setViewMode,
  setHeroIndex,
  nextHeroSlide,
  prevHeroSlide,
  setSearchQuery,
  setSearchResults,
  setSearchLoading,
  setSearchOpen,
  clearSearch,
  resetState,
  // Dashboard strips actions
  setDashboardStrips,
  setDashboardStripsLoading,
  setDashboardStripsError,
  // Trade actions
  setAvailableTrades,
  setTradesLoading,
  setTradesError,
  setTradeRow,
  setTradeRowLoading,
  setTradeRowError,
  // Bundle actions
  setBundlesLoading,
  setBundlesError,
  setBundleRow,
  setBundleRows,
  clearBundleRows,
} = discoveryHubSlice.actions;

/**
 * Thunk to load featured content for the hero banner
 */
export const loadFeaturedContent =
  (): AppThunk => async (dispatch: AppDispatch) => {
    try {
      dispatch(setFeaturedLoading(true));
      dispatch(setFeaturedError(null));

      // Import API function dynamically to avoid circular dependencies
      const { fetchFeaturedContent } = await import(
        "../api/fetchFeaturedContent"
      );
      const response = await fetchFeaturedContent();

      dispatch(setFeaturedContent(response.items));
    } catch (error) {
      console.error("Error loading featured content:", error);
      dispatch(
        setFeaturedError(
          error instanceof Error
            ? error.message
            : "Failed to load featured content",
        ),
      );
    } finally {
      dispatch(setFeaturedLoading(false));
    }
  };

/**
 * Thunk to load a specific content row
 */
export const loadContentRow =
  (rowType: RowType, limit = 10): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setRowLoading({ type: rowType, loading: true }));
      dispatch(setRowError({ type: rowType, error: null }));

      // Import API function dynamically
      const { fetchDiscoveryContent } = await import(
        "../api/fetchDiscoveryContent"
      );
      const response = await fetchDiscoveryContent(rowType, { limit });

      const row: ContentRow = {
        id: rowType,
        title: getRowTitle(rowType),
        contentType:
          rowType === "continuelearning"
            ? "continue_learning"
            : (rowType as ContentRow["contentType"]),
        items: response.items,
        totalCount: response.totalCount,
        hasMore: response.hasMore,
      };

      dispatch(setContentRow({ type: rowType, row }));
    } catch (error) {
      console.error(`Error loading ${rowType} content:`, error);
      dispatch(
        setRowError({
          type: rowType,
          error:
            error instanceof Error
              ? error.message
              : `Failed to load ${rowType} content`,
        }),
      );
    } finally {
      dispatch(setRowLoading({ type: rowType, loading: false }));
    }
  };

/**
 * Thunk to load content for a specific trade
 */
export const loadTradeContent =
  (trade: Trade): AppThunk =>
  async (dispatch: AppDispatch) => {
    // Initialize row state with loading
    dispatch(
      setTradeRow({
        tradeId: trade.id,
        tradeName: trade.long_name || trade.name,
        tradeIcon: trade.icon,
        items: [],
        loading: true,
        error: null,
        hasContent: false,
      }),
    );

    try {
      // Import API function dynamically
      const { fetchTradeContent } = await import("../api/fetchTradeContent");
      const items = await fetchTradeContent(trade.id);

      dispatch(
        setTradeRow({
          tradeId: trade.id,
          tradeName: trade.long_name || trade.name,
          tradeIcon: trade.icon,
          items,
          loading: false,
          error: null,
          hasContent: items.length > 0,
        }),
      );
    } catch (error) {
      console.error(`Error loading content for trade ${trade.name}:`, error);
      dispatch(
        setTradeRowError({
          tradeId: trade.id,
          error:
            error instanceof Error
              ? error.message
              : `Failed to load ${trade.name} content`,
        }),
      );
      dispatch(setTradeRowLoading({ tradeId: trade.id, loading: false }));
    }
  };

/**
 * Thunk to load dashboard strips (Member News, Featured, Featured Resources, Favorites)
 */
export const loadDashboardStrips =
  (): AppThunk => async (dispatch: AppDispatch) => {
    try {
      dispatch(setDashboardStripsLoading(true));
      dispatch(setDashboardStripsError(null));

      // Import API function dynamically
      const { fetchDashboardStrips } = await import(
        "../api/fetchDashboardStrips"
      );
      const data = await fetchDashboardStrips();

      dispatch(setDashboardStrips(data));
    } catch (error) {
      console.error("Error loading dashboard strips:", error);
      dispatch(
        setDashboardStripsError(
          error instanceof Error
            ? error.message
            : "Failed to load dashboard content",
        ),
      );
    } finally {
      dispatch(setDashboardStripsLoading(false));
    }
  };

/**
 * Thunk to load all trades and their content
 */
export const loadTrades = (): AppThunk => async (dispatch: AppDispatch) => {
  try {
    dispatch(setTradesLoading(true));
    dispatch(setTradesError(null));

    // Import API function dynamically
    const { fetchTrades } = await import("@/api/fetchTrades/fetchTradesApi");
    const response = await fetchTrades();

    dispatch(setAvailableTrades(response.data));

    // Load content for each trade in parallel
    await Promise.all(
      response.data.map((trade) => dispatch(loadTradeContent(trade))),
    );
  } catch (error) {
    console.error("Error loading trades:", error);
    dispatch(
      setTradesError(
        error instanceof Error ? error.message : "Failed to load trades",
      ),
    );
  } finally {
    dispatch(setTradesLoading(false));
  }
};

/**
 * Thunk to load all bundles and their tagged content
 * Bundles define content categories via resource_tags
 */
export const loadBundles = (): AppThunk => async (dispatch: AppDispatch) => {
  try {
    dispatch(setBundlesLoading(true));
    dispatch(setBundlesError(null));

    // Import API function dynamically
    const { fetchBundlesWithContent } = await import(
      "../api/fetchBundleContent"
    );
    const response = await fetchBundlesWithContent();

    dispatch(setBundleRows(response.bundles));
  } catch (error) {
    console.error("Error loading bundles:", error);
    dispatch(
      setBundlesError(
        error instanceof Error ? error.message : "Failed to load bundles",
      ),
    );
  } finally {
    dispatch(setBundlesLoading(false));
  }
};

/**
 * Thunk to load all discovery content in parallel
 * Now focused on bundles as the primary content source
 */
export const loadAllDiscoveryContent =
  (): AppThunk => async (dispatch: AppDispatch) => {
    // Load essential content in parallel:
    // - Featured content for hero banner
    // - Dashboard strips for member news/featured items
    // - Continue learning for in-progress content
    // - Bundles as the PRIMARY content source (replaces programs, courses, events, resources)
    await Promise.all([
      dispatch(loadFeaturedContent()),
      dispatch(loadDashboardStrips()),
      dispatch(loadContentRow("continuelearning")),
      dispatch(loadBundles()),
    ]);
  };

/**
 * Thunk to load content for My Learning mode
 * This fetches user-enrolled content by type
 */
export const loadMyLearningContent =
  (): AppThunk => async (dispatch: AppDispatch) => {
    // For My Learning mode, we still need content by type to show enrolled items
    await Promise.all([
      dispatch(loadContentRow("continuelearning")),
      dispatch(loadContentRow("programs")),
      dispatch(loadContentRow("learningPaths")),
      dispatch(loadContentRow("courses")),
      dispatch(loadContentRow("events")),
    ]);
  };

/**
 * Thunk to perform search across all content types
 */
export const searchDiscoveryContent =
  (query: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    if (query.length < 3) {
      dispatch(setSearchResults(null));
      return;
    }

    try {
      dispatch(setSearchLoading(true));
      dispatch(setSearchQuery(query));

      // Import API function dynamically
      const { searchContent } = await import("../api/searchContent");
      const response = await searchContent(query);

      dispatch(setSearchResults(response.results));
    } catch (error) {
      console.error("Error searching content:", error);
      dispatch(setSearchResults([]));
    } finally {
      dispatch(setSearchLoading(false));
    }
  };

/**
 * Helper function to get display title for a row type
 */
function getRowTitle(rowType: RowType): string {
  const titles: Record<RowType, string> = {
    continuelearning: "Continue Learning",
    programs: "Programs",
    learningPaths: "Learning Paths",
    courses: "Courses",
    events: "Events",
    resources: "Resources",
  };
  return titles[rowType] || rowType;
}

// Export reducer
export const discoveryHubReducer = discoveryHubSlice.reducer;
export default discoveryHubSlice.reducer;
